<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { NextButton },
  props: {
    inboxId: { type: [String, Number], required: true },
    accountId: { type: [String, Number], required: true },
  },
  emits: ['connected', 'disconnected', 'error'],
  setup(props, { emit }) {
    const qrBase64 = ref('');
    const pairingCode = ref('');
    const connectionState = ref('loading');
    const phoneNumber = ref('');
    const profileName = ref('');
    const errorMessage = ref('');
    const loading = ref(false);
    const pollTimer = ref(null);
    const pollCount = ref(0);
    const maxPolls = 10;
    const linkPhoneNumber = ref('');
    const connectMode = ref('qr'); // 'qr' or 'code'

    const apiBase = computed(
      () =>
        `/api/v1/accounts/${props.accountId}/evolution_instances/${props.inboxId}`
    );

    const isConnected = computed(() => connectionState.value === 'connected');
    const isAwaitingQr = computed(
      () => connectionState.value === 'awaiting_qr'
    );
    const isDisconnected = computed(
      () =>
        connectionState.value === 'disconnected' ||
        connectionState.value === 'failed'
    );

    const stateLabel = computed(() => {
      const labels = {
        connected: '🟢 Conectado',
        awaiting_qr: '🟡 Esperando vinculación',
        disconnected: '🔴 Desconectado',
        failed: '🔴 Error',
        loading: '⏳ Cargando...',
        creating: '⏳ Creando instancia...',
      };
      return labels[connectionState.value] || connectionState.value;
    });

    const formattedPairingCode = computed(() => {
      if (!pairingCode.value) return '';
      const code = pairingCode.value.replace(/[^A-Za-z0-9]/g, '');
      if (code.length === 8) {
        return `${code.slice(0, 4)}-${code.slice(4)}`;
      }
      return code;
    });

    function applyData(data) {
      connectionState.value = data.connection_state || 'disconnected';
      phoneNumber.value = data.phone_number || '';
      profileName.value = data.profile_name || '';
      errorMessage.value = data.error || '';
      if (data.qr_code) {
        qrBase64.value = data.qr_code;
      }
      if (data.pairing_code) {
        pairingCode.value = data.pairing_code;
      } else if (data.pairing_code === null) {
        pairingCode.value = '';
      }
      if (connectionState.value === 'connected') {
        emit('connected', { phone: phoneNumber.value });
        stopPolling();
      } else if (connectionState.value === 'failed') {
        emit('error', errorMessage.value);
      }
    }

    async function fetchStatus() {
      try {
        const { data } = await window.axios.get(apiBase.value);
        applyData(data);
      } catch (err) {
        errorMessage.value = err.response?.data?.error || err.message;
      }
    }

    async function connect() {
      loading.value = true;
      errorMessage.value = '';
      try {
        const params = {};
        // If user entered a phone number, pass it to get pairing code too
        if (linkPhoneNumber.value.trim()) {
          params.phone_number = linkPhoneNumber.value.replace(/[^0-9]/g, '');
        }
        const { data } = await window.axios.post(
          `${apiBase.value}/connect`,
          params
        );
        applyData(data);
        startPolling();
      } catch (err) {
        errorMessage.value =
          err.response?.data?.error || 'Error al conectar la instancia';
        useAlert(errorMessage.value);
      } finally {
        loading.value = false;
      }
    }

    async function reconnect() {
      loading.value = true;
      errorMessage.value = '';
      try {
        const { data } = await window.axios.post(
          `${apiBase.value}/reconnect`
        );
        applyData(data);
        startPolling();
      } catch (err) {
        errorMessage.value =
          err.response?.data?.error || 'Error al reconectar';
        useAlert(errorMessage.value);
      } finally {
        loading.value = false;
      }
    }

    async function disconnect() {
      loading.value = true;
      try {
        await window.axios.post(`${apiBase.value}/disconnect`);
        connectionState.value = 'disconnected';
        qrBase64.value = '';
        pairingCode.value = '';
        phoneNumber.value = '';
        emit('disconnected');
      } catch (err) {
        errorMessage.value =
          err.response?.data?.error || 'Error al desconectar';
        useAlert(errorMessage.value);
      } finally {
        loading.value = false;
      }
    }

    async function refreshQr() {
      loading.value = true;
      errorMessage.value = '';
      try {
        const params = {};
        if (linkPhoneNumber.value.trim()) {
          params.phone_number = linkPhoneNumber.value.replace(/[^0-9]/g, '');
        }
        const { data } = await window.axios.get(
          `${apiBase.value}/qr_code`,
          { params }
        );
        if (data.qr_code) {
          qrBase64.value = data.qr_code;
          connectionState.value = 'awaiting_qr';
        }
        if (data.pairing_code) {
          pairingCode.value = data.pairing_code;
        }
      } catch (err) {
        errorMessage.value =
          err.response?.data?.error || 'Error al obtener QR';
      } finally {
        loading.value = false;
      }
    }

    async function requestPairingCode() {
      if (!linkPhoneNumber.value.trim()) {
        useAlert('Ingresá el número de teléfono con código de país');
        return;
      }
      loading.value = true;
      errorMessage.value = '';
      try {
        const phone = linkPhoneNumber.value.replace(/[^0-9]/g, '');
        const { data } = await window.axios.get(
          `${apiBase.value}/qr_code`,
          { params: { phone_number: phone } }
        );
        if (data.pairing_code) {
          pairingCode.value = data.pairing_code;
          connectionState.value = 'awaiting_qr';
          startPolling();
        } else {
          errorMessage.value =
            'No se pudo obtener el código. Verificá el número e intentá de nuevo.';
        }
        if (data.qr_code) {
          qrBase64.value = data.qr_code;
        }
      } catch (err) {
        errorMessage.value =
          err.response?.data?.error || 'Error al obtener código';
        useAlert(errorMessage.value);
      } finally {
        loading.value = false;
      }
    }

    function startPolling() {
      stopPolling();
      pollCount.value = 0;
      pollTimer.value = setInterval(() => {
        pollCount.value += 1;
        if (pollCount.value >= maxPolls) {
          stopPolling();
          if (!isConnected.value) {
            errorMessage.value =
              'Tiempo de espera agotado. Presioná "Obtener código" o "Actualizar QR" para reintentar.';
          }
          return;
        }
        fetchStatus();
      }, 15000);
    }

    function stopPolling() {
      if (pollTimer.value) {
        clearInterval(pollTimer.value);
        pollTimer.value = null;
      }
      pollCount.value = 0;
    }

    onMounted(async () => {
      await fetchStatus();
      if (!isConnected.value) {
        startPolling();
      }
    });

    onBeforeUnmount(() => stopPolling());

    return {
      qrBase64,
      pairingCode,
      formattedPairingCode,
      connectionState,
      phoneNumber,
      profileName,
      errorMessage,
      loading,
      isConnected,
      isAwaitingQr,
      isDisconnected,
      stateLabel,
      linkPhoneNumber,
      connectMode,
      connect,
      reconnect,
      disconnect,
      refreshQr,
      requestPairingCode,
      fetchStatus,
    };
  },
};
</script>

<template>
  <div class="evolution-qr-panel p-4 rounded-lg border border-n-weak bg-n-solid-1">
    <!-- Status header -->
    <div class="flex items-center justify-between mb-4">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          WhatsApp QR
        </h3>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ stateLabel }}
          <span v-if="phoneNumber && isConnected" class="ml-2 font-mono">
            {{ phoneNumber }}
          </span>
        </p>
        <p v-if="profileName && isConnected" class="text-xs text-n-slate-10">
          {{ profileName }}
        </p>
      </div>
    </div>

    <!-- Error message -->
    <div
      v-if="errorMessage"
      class="mb-4 p-3 rounded bg-n-ruby-2 border border-n-ruby-6 text-n-ruby-11 text-sm"
    >
      {{ errorMessage }}
    </div>

    <!-- Connection mode toggle (only when awaiting or disconnected) -->
    <div v-if="isAwaitingQr || isDisconnected" class="mb-4">
      <div class="flex rounded-lg border border-n-weak overflow-hidden">
        <button
          class="flex-1 py-2 px-4 text-sm font-medium transition-colors"
          :class="connectMode === 'qr'
            ? 'bg-n-brand text-white'
            : 'bg-n-solid-1 text-n-slate-11 hover:bg-n-solid-3'"
          @click="connectMode = 'qr'"
        >
          📷 Código QR
        </button>
        <button
          class="flex-1 py-2 px-4 text-sm font-medium transition-colors"
          :class="connectMode === 'code'
            ? 'bg-n-brand text-white'
            : 'bg-n-solid-1 text-n-slate-11 hover:bg-n-solid-3'"
          @click="connectMode = 'code'"
        >
          🔢 Código numérico
        </button>
      </div>
    </div>

    <!-- QR mode -->
    <div v-if="connectMode === 'qr' && isAwaitingQr && qrBase64" class="text-center mb-4">
      <p class="text-sm text-n-slate-11 mb-3">
        Escaneá este código QR con WhatsApp en tu teléfono
      </p>
      <div class="inline-block p-4 bg-white rounded-lg shadow-md">
        <img
          :src="qrBase64.startsWith('data:') ? qrBase64 : `data:image/png;base64,${qrBase64}`"
          alt="WhatsApp QR Code"
          class="w-64 h-64"
        />
      </div>
      <p class="text-xs text-n-slate-10 mt-2">
        WhatsApp → ⋮ Menú → Dispositivos vinculados → Vincular dispositivo
      </p>
    </div>

    <!-- Pairing code mode -->
    <div v-if="connectMode === 'code' && (isAwaitingQr || isDisconnected)" class="mb-4">
      <!-- Phone input -->
      <div class="mb-4">
        <label class="text-sm text-n-slate-11 block mb-1">
          Número de teléfono a vincular (con código de país)
        </label>
        <div class="flex gap-2">
          <input
            v-model="linkPhoneNumber"
            type="text"
            placeholder="Ej: 5491112345678"
            class="flex-1 p-2 border border-n-weak rounded text-sm font-mono"
            @keyup.enter="requestPairingCode"
          />
          <NextButton
            :loading="loading"
            size="small"
            @click="requestPairingCode"
          >
            Obtener código
          </NextButton>
        </div>
        <p class="text-xs text-n-slate-10 mt-1">
          Sin +, sin espacios, sin guiones. Ej: 5491112345678
        </p>
      </div>

      <!-- Display pairing code -->
      <div
        v-if="pairingCode"
        class="p-4 rounded-lg bg-n-blue-2 border border-n-blue-6 text-center"
      >
        <p class="text-sm text-n-slate-11 mb-2">
          Ingresá este código en tu teléfono:
        </p>
        <p class="text-3xl font-mono font-bold text-n-blue-11 tracking-[0.3em] select-all">
          {{ formattedPairingCode }}
        </p>
        <div class="mt-3 text-xs text-n-slate-10 space-y-1">
          <p>WhatsApp → ⋮ Menú → Dispositivos vinculados → Vincular dispositivo</p>
          <p>→ Tocá "Vincular con número de teléfono" en la parte inferior</p>
        </div>
      </div>

      <div
        v-if="!pairingCode && isAwaitingQr"
        class="p-4 rounded bg-n-amber-2 border border-n-amber-6 text-center text-sm text-n-amber-11"
      >
        Ingresá tu número y tocá "Obtener código" para recibir un código de vinculación de 8 dígitos.
      </div>
    </div>

    <!-- Also show pairing code in QR mode if available -->
    <div
      v-if="connectMode === 'qr' && isAwaitingQr && pairingCode"
      class="mb-4 p-3 rounded bg-n-blue-2 border border-n-blue-6 text-center"
    >
      <p class="text-xs text-n-slate-10 mb-1">
        También podés vincular con código:
      </p>
      <p class="text-xl font-mono font-bold text-n-blue-11 tracking-[0.2em] select-all">
        {{ formattedPairingCode }}
      </p>
    </div>

    <!-- Connected state -->
    <div
      v-if="isConnected"
      class="mb-4 p-3 rounded bg-n-green-2 border border-n-green-6 text-n-green-11 text-sm"
    >
      ✅ WhatsApp conectado correctamente. Los mensajes se envían y reciben por este canal.
    </div>

    <!-- Disconnected state -->
    <div
      v-if="isDisconnected && connectMode === 'qr'"
      class="mb-4 p-3 rounded bg-n-amber-2 border border-n-amber-6 text-n-amber-11 text-sm"
    >
      La sesión se desconectó. Conectá de nuevo para escanear un nuevo QR.
    </div>

    <!-- Action buttons -->
    <div class="flex gap-2 flex-wrap">
      <NextButton
        v-if="isDisconnected || connectionState === 'loading'"
        :loading="loading"
        @click="connect"
      >
        🔗 Conectar
      </NextButton>

      <NextButton
        v-if="isAwaitingQr && connectMode === 'qr'"
        variant="secondary"
        :loading="loading"
        @click="refreshQr"
      >
        🔄 Actualizar QR
      </NextButton>

      <NextButton
        v-if="isConnected"
        variant="secondary"
        :loading="loading"
        @click="reconnect"
      >
        🔄 Reconectar
      </NextButton>

      <NextButton
        v-if="isConnected || isAwaitingQr"
        variant="secondary"
        :loading="loading"
        @click="disconnect"
      >
        ❌ Desconectar
      </NextButton>

      <NextButton variant="secondary" :loading="loading" @click="fetchStatus">
        📡 Verificar estado
      </NextButton>
    </div>
  </div>
</template>
